import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSession } from '../auth/session.tsx';
import type { MapPlace } from '../api/types.ts';
import { fitFor } from './map-fit.ts';
import { paceColour, tokenColour } from '../tokens.ts';

/**
 * The caseload on a map.
 *
 * An ADDITIONAL view, never a replacement. The facility board (`view=locations`)
 * answers "which places are behind, worst first" and answers it well and fast;
 * this answers "where are they", which a list cannot. Both read the same
 * statement and the same clients — see `directorStatement` in
 * `api/src/routes/dashboards.ts`, where the map's source is chosen and the
 * reason is written down at length.
 *
 * ---------------------------------------------------------------------------
 *  THE TILES COME FROM OUR OWN API, AND THAT IS THE POINT
 * ---------------------------------------------------------------------------
 *
 * Pointing Leaflet at OpenStreetMap would tell a third-party tile server which
 * streets are being looked at — and on a screen covered in pins for
 * guardianship clients, that is a rough picture of where they live. It would
 * undo the reason Azure Maps was chosen for geocoding in the first place.
 *
 * So tiles come from `/api/tiles/...`, which fetches them from Azure Maps with
 * the App Service managed identity and caches them. Leaflet's own `TileLayer`
 * cannot be used as-is: it sets `img.src`, which sends no `Authorization`
 * header, so every tile would come back 401. `createTile` is overridden to
 * fetch the tile properly and hand the bytes to the image as a blob URL.
 *
 * **Every blob URL is revoked when Leaflet discards the tile.** Object URLs
 * live until revoked, so a map that is panned around for an afternoon would
 * otherwise hold every tile it had ever drawn — the same "only ever grows"
 * shape as the tile cache on disk, and the audit partitions before it.
 */

/**
 * Where the map opens when there is nothing to fit: the five boroughs and
 * Nassau, where almost the whole caseload is. A filter that matches nobody
 * opens here, never on the last place looked at.
 */
export const DEFAULT_CENTRE: [number, number] = [40.7, -73.87];
export const DEFAULT_ZOOM = 11;
/** One place: close enough to read the streets around it, far enough to see the neighbourhood. */
export const SINGLE_ZOOM = 15;

/** Matched to the server's cap. Asking outside it is refused there, not here. */
export const MIN_ZOOM = 9;
export const MAX_ZOOM = 17;

/**
 * The wheel zooms whenever the pointer is over a map, on every map (Arthur, 22 October, A-#50; it was off until a
 * button beside the map turned it on, and that button is gone). Off the map the wheel scrolls the page as ever. The
 * +/- buttons stay, at 44 px.
 */
export const WHEEL_ZOOM = true;

/**
 * A tile layer that authenticates.
 *
 * Built inside the component so it closes over the session's `getToken`, which
 * is a hook value and cannot be read from a module-level class.
 */
export function authenticatedTiles(getToken: () => Promise<string>): L.TileLayer {
  const Authenticated = L.TileLayer.extend({
    createTile(this: L.TileLayer, coords: L.Coords, done: L.DoneCallback) {
      const img = document.createElement('img');
      img.alt = '';
      // Decorative: the map's meaning is in the pins, which are real DOM
      // elements with text. A screen reader announcing 40 tile images would
      // bury them.
      img.setAttribute('role', 'presentation');

      let objectUrl: string | null = null;
      // Leaflet discards tiles as they leave the viewport. Without this the
      // page holds every tile it has ever drawn, for the life of the tab.
      const revoke = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };
      img.addEventListener('load', () => {
        // Safe once decoded: the bitmap is the browser's now.
        if (img.complete) revoke();
      });

      void (async () => {
        try {
          const token = await getToken();
          const response = await fetch(
            `/api/tiles/microsoft.base.road/${coords.z}/${coords.x}/${coords.y}`,
            { headers: { authorization: `Bearer ${token}` } },
          );
          if (!response.ok) throw new Error(`tile ${response.status}`);
          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          img.src = objectUrl;
          done(undefined, img);
        } catch (err) {
          revoke();
          // Leaflet leaves a gap where the tile would be, which is the right
          // outcome: a missing basemap tile must not take the pins with it.
          done(err as Error, img);
        }
      })();

      return img;
    },
  });
  return new (Authenticated as unknown as new (url: string, opts: L.TileLayerOptions) => L.TileLayer)(
    '',
    {
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      tileSize: 256,
      // Required by Azure Maps' terms and by simple good manners.
      attribution: '© Microsoft, © TomTom',
    },
  );
}

export function CaseloadMap({
  places,
  fitKey,
  onOpenPlace,
}: {
  places: MapPlace[];
  /**
   * The filters these places answer, or null while the places on screen are a
   * previous answer kept as a placeholder. The map fits itself when this
   * CHANGES, and at no other time: a reload, a poll or a tile arriving keeps
   * whatever the reader has panned to.
   */
  fitKey: string | null;
  onOpenPlace: (place: MapPlace) => void;
}) {
  const { getToken } = useSession();
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef<L.LayerGroup | null>(null);
  const [failed, setFailed] = useState(false);
  /** The filters the view was last fitted to. */
  const fitted = useRef<string | null>(null);
  /** Pins left outside the view the fit chose, said in words under the map. */
  const [outside, setOutside] = useState(0);

  // The token getter changes identity between renders; the layer must not be
  // rebuilt for that. A ref keeps the latest without re-running the effect.
  const token = useRef(getToken);
  token.current = getToken;

  const open = useRef(onOpenPlace);
  open.current = onOpenPlace;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: DEFAULT_CENTRE,
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      scrollWheelZoom: WHEEL_ZOOM,
    });
    instance.addLayer(authenticatedTiles(() => token.current()));
    markers.current = L.layerGroup().addTo(instance);
    map.current = instance;

    return () => {
      instance.remove();
      map.current = null;
      markers.current = null;
    };
  }, []);

  // Pins follow the data. Rebuilt wholesale rather than diffed: a few hundred
  // circle markers is nothing, and diffing is where stale pins come from.
  useEffect(() => {
    const layer = markers.current;
    const instance = map.current;
    if (!layer || !instance) return;
    layer.clearLayers();

    // The reader's own places, which the view is fitted to. A building shown
    // only for the agency's other clients there is context, not the filtered
    // set: fitting to it would open every case manager on the whole agency.
    const theirs: [number, number][] = [];
    for (const place of places) {
      const lat = Number(place.latitude);
      const lon = Number(place.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      if (place.client_count > 0) theirs.push([lat, lon]);

      // Area scales with the number of clients, not radius — a place with four
      // people should look four times as big, and doubling the radius would
      // make it look sixteen.
      const radius = Math.min(26, 7 + Math.sqrt(place.client_count) * 3.2);

      const marker = L.circleMarker([lat, lon], {
        radius,
        // Never colour alone: the count is drawn in the tooltip and the
        // never-seen ring is a shape, not a hue.
        color: place.never_seen > 0 ? tokenColour('pin-alarm') : tokenColour('pin-edge'),
        weight: place.never_seen > 0 ? 3 : 1,
        fillColor: paceColour(place.average_pace),
        fillOpacity: 0.85,
      });

      const name = place.location ?? 'Location not recorded';
      // A building's others: a number, never names (109's facility page rule).
      const others = Number(place.others ?? 0);
      const yours = others > 0
        ? `${place.client_count} of your client${place.client_count === 1 ? '' : 's'}, ${others} other${others === 1 ? '' : 's'}`
        : `${place.client_count} client${place.client_count === 1 ? '' : 's'}`;
      marker.bindTooltip(
        `<strong>${escapeHtml(name)}</strong><br>${yours}${
          place.never_seen > 0 ? `<br><strong>${place.never_seen} never seen</strong>` : ''}`,
        { direction: 'top' },
      );
      marker.on('click', () => open.current(place));
      // Keyboard: a map nobody can tab through is a map half the staff cannot
      // use. Leaflet gives circle markers no focus by default.
      marker.on('keypress', (event) => {
        const original = (event as unknown as { originalEvent?: KeyboardEvent }).originalEvent;
        if (original?.key === 'Enter' || original?.key === ' ') open.current(place);
      });
      layer.addLayer(marker);
    }

    // FILTER FIRST, THEN FIT. `places` is already the filtered set, from the
    // server, for this caller: a case manager's map opens on their own
    // caseload, and the Director's on the agency. Fitted when the filters
    // change, never when the same filters are reloaded.
    if (fitKey === null || fitKey === fitted.current) return;
    fitted.current = fitKey;
    try {
      const fit = fitFor(theirs);
      if (!fit) instance.setView(DEFAULT_CENTRE, DEFAULT_ZOOM, { animate: false });
      else if ('centre' in fit) instance.setView(fit.centre, SINGLE_ZOOM, { animate: false });
      else instance.fitBounds(L.latLngBounds(fit.box), { padding: [40, 40], maxZoom: 14, animate: false });
      const view = instance.getBounds();
      setOutside(theirs.filter((p) => !view.contains(p)).length);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [places, fitKey]);

  return (
    <div className="relative">
      <div
        ref={container}
        className="h-[32rem] w-full rounded-lg bg-neutral-200 lg:h-[40rem]"
        // Leaflet needs a real height before it will lay out; the class above
        // provides it. Announced as an image region so a screen reader does not
        // walk hundreds of tiles.
        role="region"
        aria-label="Map of where clients are now"
      />
      {failed && (
        <p role="status" className="mt-2 text-sm text-amber-900">
          The map could not fit to these places. The list below is complete.
        </p>
      )}
      {!failed && outside > 0 && (
        <p role="status" className="mt-2 text-sm text-neutral-700">
          {outside} {outside === 1 ? 'place' : 'places'} with your clients {outside === 1 ? 'was' : 'were'} left outside
          the map as it opened, far from the rest. Zoom out to see {outside === 1 ? 'it' : 'them'}.
        </p>
      )}
    </div>
  );
}

/** Tooltips take HTML, and a place name is data. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}
